// src/styles/controllerPageStyles.js
import { styled } from 'baseui';
import { withStyle } from 'styletron-react';
import { defaultAdHeight, mobileAdHeight } from './displayPageStyles';

export const PageContainer = styled('div', ({ $theme }) => ({
  padding: 'clamp(16px, 2vw, 32px)',
  backgroundColor: $theme.colors.backgroundPrimary,
  color: $theme.colors.contentPrimary,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
}));

export const SectionContainer = styled('div', ({ $theme }) => ({
  margin: 'clamp(16px, 2vw, 24px) 0',
  border: `1px solid ${$theme.colors.borderOpaque}`,
  borderRadius: $theme.borders.radius400,
  backgroundColor: $theme.colors.backgroundTertiary,
  padding: 'clamp(16px, 2vw, 24px)',
}));

export const ResponsiveTournamentControlContainer = styled('div', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: $theme.sizing.scale600,
  gap: $theme.sizing.scale500,
  '> :first-child': {
    flex: '1 1 200px',
    minWidth: '200px',
  },
  '> :last-child': {
    flex: '0 0 auto',
  },
  [`@media screen and (max-width: ${$theme.breakpoints.medium}px)`]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: $theme.sizing.scale300,
  },
}));

export const FlexColumnCentered = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

export const ActionButtonsContainer = styled('div', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.scale200,
  justifyContent: 'flex-end',
  [`@media screen and (max-width: ${$theme.breakpoints.medium}px)`]: {
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
}));

export const HiddenInput = styled('input', {
  display: 'none',
});

export const StyledHr = styled('hr', ({ $theme }) => ({
    width: '100%',
    margin: `${$theme.sizing.scale600} 0`,
    border: 'none',
    borderTop: `1px solid ${$theme.colors.borderOpaque}`,
}));

export const ScrollableTableWrapper = styled('div', {
  width: '100%',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
});

export const TableContainer = styled('table', {
  width: '100%',
  borderCollapse: 'collapse',
});

export const HeaderRow = styled('tr', ({ $theme }) => ({
    backgroundColor: $theme.colors.backgroundSecondary,
    textAlign: 'center',
}));

export const HeaderCell = styled('th', ({ $theme }) => ({
  padding: 'clamp(0.25rem, 0.5vw, 0.75rem)',
  border: `1px solid ${$theme.colors.borderOpaque}`,
  backgroundColor: $theme.colors.tableHeader,
  color: $theme.colors.contentPrimary,
  textAlign: 'center',
  position: 'sticky',
  top: 0,
}));

export const StyledDraggableHeaderCell = withStyle(HeaderCell, ({ $transform, $transition, $isDragging, $canEditColumns, $isFixedColumn }) => ({
  transform: $transform,
  transition: $transition,
  opacity: $isDragging ? 0.8 : 1,
  cursor: !$canEditColumns || $isFixedColumn ? 'default' : 'grab',
}));

export const HeaderStartCell = withStyle(HeaderCell, ({ $theme }) => ({
    width: $theme.sizing.scale1000,
}));

export const HeaderContent = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
});

export const Label = styled('span', {
  flexGrow: 1,
});

export const Cell = styled('td', ({ $theme }) => ({
  padding: 'clamp(0.25rem, 0.5vw, 0.75rem)',
  border: `1px solid ${$theme.colors.borderOpaque}`,
  textAlign: 'center',
}));

export const DragHandleCell = withStyle(Cell, {
  cursor: 'grab',
  width: 'clamp(30px, 5vw, 60px)',
});

export const StyledDraggableRow = styled('tr', ({ $transform, $transition, $isDragging }) => ({
  transform: $transform,
  transition: $transition,
  opacity: $isDragging ? 0.7 : 1,
  backgroundColor: $isDragging ? '#f0f0f0' : undefined,
}));

export const StyledDragHandleCell = withStyle(DragHandleCell, {
  touchAction: 'none',
});

export const OverlayTableContainer = withStyle(TableContainer, ({ $theme }) => ({
    backgroundColor: $theme.colors.backgroundTertiary,
    border: `2px dashed ${$theme.colors.borderAccent}`,
    opacity: 0.8,
}));

export const OverlayCell = styled('td', ({ $theme }) => ({
  padding: $theme.sizing.scale200,
  border: `1px solid ${$theme.colors.borderOpaque}`,
  backgroundColor: $theme.colors.backgroundSecondary,
  textAlign: 'center',
}));

export const MenuContainer = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale100,
  padding: $theme.sizing.scale200,
  backgroundColor: $theme.colors.backgroundPrimary,
  borderRadius: $theme.borders.radius400,
  border: `1px solid ${$theme.colors.borderOpaque}`,
}));

export const ModalBodyContainer = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale500,
}));

export const ModalSectionHeader = styled('h4', ({ $theme }) => ({
  color: $theme.colors.contentPrimary,
  marginTop: $theme.sizing.scale800,
  marginBottom: $theme.sizing.scale500,
  borderBottom: `1px solid ${$theme.colors.borderOpaque}`,
  paddingBottom: $theme.sizing.scale200,
}));

export const ThemeButtonContainer = styled('div', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.scale300,
}));

export const SettingsLabel = styled('label', ({ $theme }) => ({
  display: 'block',
  color: $theme.colors.contentPrimary,
  marginBottom: $theme.sizing.scale200,
}));

export const SettingsIndentedContainer = styled('div', ({ $theme }) => ({
  marginLeft: $theme.sizing.scale800,
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale300,
}));

export const EditorRowContainer = styled('div', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.scale300,
}));

export const DeleteButtonOverrides = {
  BaseButton: {
    style: ({ $theme }) => ({
      color: $theme.colors.negative,
      height: '36px',
      minWidth: '52px',
    }),
  },
};

export const ShareModalContent = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: $theme.sizing.scale800,
}));

export const ShareUrlInput = styled('div', ({ $theme }) => ({
  display: 'flex',
  width: '100%',
  gap: $theme.sizing.scale300,
}));

export const QrCodeWrapper = styled('div', ({ $theme }) => ({
  padding: $theme.sizing.scale600,
  backgroundColor: 'white',
  borderRadius: $theme.borders.radius400,
  display: 'inline-block',
}));

export const IntegratedViewContainer = styled('div', ({ $isAdVisible, $palette }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: $palette.backgroundPrimary,
}));

export const IntegratedViewContent = styled('div', ({ $isAdVisible, $theme }) => ({
  flex: '1 1 auto',
  height: $isAdVisible ? `calc(100% - ${defaultAdHeight})` : '100%',
  [`@media screen and (max-width: ${$theme.breakpoints.medium - 1}px), screen and (min-width: ${$theme.breakpoints.medium}px) and (max-width: ${$theme.breakpoints.large - 1}px) and (orientation: portrait)`]: {
    height: $isAdVisible ? `calc(100% - ${mobileAdHeight})` : '100%',
  },
  display: 'flex',
  flexDirection: 'column',
}));

export const LandscapeControls = styled('div', ({ $theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: $theme.sizing.scale400,
  padding: $theme.sizing.scale400,
  alignContent: 'center',
  height: '100%',
}));

export const TournamentNameInputOverrides = {
  Root: {
    style: ({ $theme }) => ({
      marginRight: $theme.sizing.scale600,
      [`@media screen and (max-width: ${$theme.breakpoints.medium}px)`]: {
        marginRight: '0px',
        marginBottom: $theme.sizing.scale400,
      },
    }),
  },
};

export const PortraitControlsFloatingBar = styled('div', ({ $theme }) => ({
  position: 'fixed',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 100,
  display: 'flex',
  gap: $theme.sizing.scale600,
  backgroundColor: 'rgba(0,0,0,0.5)',
  padding: $theme.sizing.scale400,
  borderRadius: $theme.borders.radius200,
}));

export const BackToControlsButton = styled('div', ({ $theme }) => ({
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  zIndex: 101,
  opacity: 0.7,
}));


